import Modal from '@organisms/Modal/Modal';
import { useTranslations } from 'next-intl';
import Text from '@atoms/Text/Text';
import { discountTypes } from '@molecules/PersonalElementsCards/PersonalElementsVoucher/PersonalElementsVoucher';
import { AxfoodExternalVoucher } from '@occ/api-client';
import { StyledTextWithMargin, StyledVoucherHeading } from './VoucherModal.styles';
import { useMemo } from 'react';
import getVoucherType from '@helpers/getVoucherType';
import { getVoucherTypeKey } from '@helpers/voucherModalTranslation';

interface Props {
  voucher: AxfoodExternalVoucher;
  onClose: () => void;
  onConfirm: () => void;
}

const VoucherModal = ({ voucher, onClose, onConfirm }: Props) => {
  const t = useTranslations('vouchermodal');

  const isOnlineOnly = useMemo(() => {
    if (voucher?.voucherChannelData) {
      const { online, omni, selfcheckout, selfscan, checkout } = voucher.voucherChannelData;
      return online && !omni && !selfcheckout && !selfscan && !checkout;
    }
    return false;
  }, [voucher]);

  const voucherType = getVoucherType(voucher);

  const typeKeys = {
    text: getVoucherTypeKey(voucherType, 'text'),
    requirements: getVoucherTypeKey(voucherType, 'requirements'),
    validIn: getVoucherTypeKey(voucherType, 'validIn'),
  };

  return (
    <Modal
      size="small"
      onClose={onClose}
      onConfirm={onConfirm}
      confirmButtonText={t('button')}
      closeOnEscape
      buttonWidth={100}
      hasCloseIcon
    >
      <StyledVoucherHeading variant="h2" size="small">
        {t('title', {
          value: voucher.discountValue ?? '',
          unit: voucher.discountType === discountTypes.ABSOLUTE ? t('absoluteValue') : t('percentageValue'),
        })}
      </StyledVoucherHeading>

      <StyledTextWithMargin type="body" size="small">
        {t(typeKeys.text)}
      </StyledTextWithMargin>
      <Text type="body" size="small" data-testid="voucher-requirements-and-validIn">
        {!!voucher.minPurchaseAmount &&
          t(typeKeys.requirements, {
            value: voucher.minPurchaseAmount,
          })}
        {t(typeKeys.validIn)}
      </Text>
      <StyledTextWithMargin type="body" size="small">
        {t('validUntil', {
          date: voucher.expirationDate ?? '',
        })}
      </StyledTextWithMargin>
      {!isOnlineOnly && (
        <>
          <Text type="label">{t('code')}</Text>
          <StyledTextWithMargin type="price" size="large" color="red">
            {voucher.voucherNbr}
          </StyledTextWithMargin>
        </>
      )}
    </Modal>
  );
};

export default VoucherModal;
